import templateReplace from 'es6-template-strings';
import { func, shape, string } from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import queryString from 'query-string';
import styled from 'styled-components';

import { get } from 'ravenjs/utils/lodash';
import Typography from 'ravenjs/lib/Typography';
import Row from 'ravenjs/lib/Row';
import { stringReplaceWithJSX } from 'ravenjs/utils/string';
import { getThemeProps, themeGet } from 'ravenjs/utils/theme';
import Container from 'ravenjs/lib/Container';
import Link from 'ravenjs/lib/Link';
import { extractErrorMessage } from 'ravenjs/utils/error';
import Form from 'ravenjs/lib/Form';
import { Col } from 'ravenjs/lib';

import NavLogo from 'components/NavLogo';
import { DEFAULT_COLORS } from 'constants/colors';
import { HISTORY } from 'constants/proptypes';
import MESSAGES from 'constants/messages';
import { MODALS } from 'constants/modals';
import { actions as brandingActions } from 'modules/branding';
import { actions as uiActions } from 'modules/ui';
import { actions as userActions } from 'modules/user';
import { actions as pendoActions } from 'modules/pendo';
import { MNL_INTELLIGENCE } from 'constants/intelligence';
import { EVENTS } from 'constants/events';
import REGEX_VALIDATE from 'constants/regex';

import schema from './schema.json';

const DescriptionStyled = styled.div`
    max-width: 100%;
    width: 600px;
`;

const HeaderStyled = styled.header`
    align-items: center;
    display: flex;
    justify-content: center;
    height: ${themeGet('AppNavigation.height', 62)}px;
    left: 0;
    width: 100%;
    ${getThemeProps('AppNavigation.styles')};
`;

const Unsubscribe = ({
    checkUserSubscriptionStatus,
    fetchBranding,
    history,
    isLoading,
    location,
    openModal,
    sendPendoEvent,
    unsubscribeUserFromSubscriptions,
}) => {
    const UNSUBSCRIBE_STATUS = {
        DEFAULT: 'DEFAULT',
        INVALID: 'INVALID',
        SUCCESS: 'SUCCESS',
    };

    const formRef = React.useRef();
    const queryParams = get(location, 'search', '');
    const queryParamsParse = queryString.parse(queryParams);
    const token = get(queryParamsParse, 'token', null);
    const emailValue = get(queryParamsParse, 'email', null);
    const emailParam = emailValue ? decodeURIComponent(emailValue) : null;
    const isProspect = get(queryParamsParse, 'isProspect', null);
    const [branding, setBranding] = React.useState({});
    const [status, setStatus] = React.useState(null);
    const [formData, setFormData] = React.useState({});
    const [hasError, setError] = React.useState(true);
    const [liveValidate, setLiveValidate] = React.useState(true);

    const messages = get(schema, `${status}.messages`, []);
    const subtitle = get(schema, `${status}.subtitle`, []);
    const title = get(schema, `${status}.title`, []);
    const platformName = get(branding, 'platformName', null);

    const getDefaultBranding = React.useCallback(async () => {
        const domain = MAESTER.companies.root_company_id;
        const resp = await fetchBranding(domain);
        setBranding(resp);
    }, [fetchBranding]);

    const getBranding = React.useCallback(async () => {
        try {
            const subdomain = window.location.host.split('.')[0];
            if (subdomain) {
                const resp = await fetchBranding(subdomain);
                setBranding(resp);
            } else {
                getDefaultBranding();
            }
        } catch (e) {
            getDefaultBranding();
        }
    }, [fetchBranding, getDefaultBranding]);

    const loadData = React.useCallback(async () => {
        try {
            isLoading(true);
            await getBranding();

            if (token || emailParam) {
                const queryParams = get(history, 'location.search', '');
                const queryParamsParse = queryString.parse(queryParams);
                const source = get(queryParamsParse, 'source', null);
                if (source === MNL_INTELLIGENCE.PENDO_SOURCE_MNL) {
                    const pendoEvent = EVENTS.INTELLIGENCE.REDIRECT_TO_UNSUBSCRIBE_FROM_EMAIL;
                    sendPendoEvent(pendoEvent);
                }
                const { message } = await checkUserSubscriptionStatus({
                    token,
                    isProspect,
                    email: emailParam,
                });

                if (message === 'true') {
                    setStatus(UNSUBSCRIBE_STATUS.SUCCESS);
                } else {
                    setStatus(UNSUBSCRIBE_STATUS.DEFAULT);
                }
            } else {
                setStatus(UNSUBSCRIBE_STATUS.INVALID);
            }
        } catch (e) {
            setStatus(UNSUBSCRIBE_STATUS.INVALID);
        } finally {
            isLoading(false);
        }
    }, [
        isLoading,
        getBranding,
        token,
        emailParam,
        history,
        checkUserSubscriptionStatus,
        isProspect,
        sendPendoEvent,
        UNSUBSCRIBE_STATUS.SUCCESS,
        UNSUBSCRIBE_STATUS.DEFAULT,
        UNSUBSCRIBE_STATUS.INVALID,
    ]);

    const handleUnsubscribe = async () => {
        const { email } = formData;
        try {
            isLoading(true);
            await unsubscribeUserFromSubscriptions({ isProspect, token, email });
            setStatus(UNSUBSCRIBE_STATUS.SUCCESS);
        } catch (e) {
            openModal(MODALS.CONFIRM, {
                title: MESSAGES.MODAL.CONFIRM.TITLE.ERROR,
                description: extractErrorMessage(e),
            });
        } finally {
            isLoading(false);
        }
    };

    const onClickMyProfile = () => {
        history.push('/profile/details');
    };

    const renderDescription = () => {
        return messages.map(message => (
            <Typography gutterTop="1rem" key={message}>
                {stringReplaceWithJSX(
                    templateReplace(message, { PLATFORM_NAME: platformName || '' }),
                    {
                        MY_PROFILE: (
                            <Link
                                fontSize="0.875rem"
                                key="my_profile"
                                onClick={onClickMyProfile}
                                style={{
                                    color: DEFAULT_COLORS.LINK,
                                }}
                            >
                                My Profile
                            </Link>
                        ),
                    }
                )}
            </Typography>
        ));
    };

    const renderSubtitle = () => {
        return templateReplace(subtitle, { PLATFORM_NAME: platformName || '' });
    };

    const renderTitle = () => {
        return templateReplace(title, { PLATFORM_NAME: platformName || '' });
    };

    const handleOnChange = ({ formData: data }) => {
        setLiveValidate(true);
        setFormData(data);
    };

    const validate = (data, errors) => {
        const { email = '' } = formData;
        if (!email.match(REGEX_VALIDATE.EMAIL_ADDRESS)) {
            errors.email.addError(MESSAGES.FORM.INCORRECT_EMAIL);
            setError(true);
        } else {
            setError(false);
        }
        return errors;
    };

    React.useEffect(() => {
        loadData();
    }, [loadData]);

    React.useEffect(() => {
        liveValidate && setLiveValidate(false);
    }, [liveValidate]);

    const logoUrl = get(branding, 'config.logoUrl', null);
    return (
        status && (
            <>
                <HeaderStyled>
                    <NavLogo src={logoUrl} width="auto" height="100%" />
                </HeaderStyled>
                <Container margin="4rem auto">
                    <Row margin="0">
                        <Typography type="h1">{renderTitle()}</Typography>
                    </Row>
                    {subtitle && (
                        <Row margin="0">
                            <Typography type="subheader" gutterTop="10px" gutterBottom="0">
                                {renderSubtitle()}
                            </Typography>
                        </Row>
                    )}
                    <Row margin="0">
                        <DescriptionStyled>{renderDescription()}</DescriptionStyled>
                    </Row>
                    {status === UNSUBSCRIBE_STATUS.DEFAULT && (
                        <>
                            <Row margin="1rem 0 0 0">
                                <Col padding="0" size={4}>
                                    <Form
                                        ref={formRef}
                                        formData={formData}
                                        {...schema.FORM_SCHEMA}
                                        autocomplete="off"
                                        onChange={handleOnChange}
                                        validate={validate}
                                        showErrorList={false}
                                        liveValidate={liveValidate}
                                        actionsList={[
                                            {
                                                children: 'Unsubscribe',
                                                color: 'primary',
                                                onClick: handleUnsubscribe,
                                                disabled: hasError,
                                            },
                                        ]}
                                    />
                                </Col>
                            </Row>
                        </>
                    )}
                </Container>
            </>
        )
    );
};

Unsubscribe.propTypes = {
    checkUserSubscriptionStatus: func.isRequired,
    fetchBranding: func.isRequired,
    location: shape({
        search: string.isRequired,
    }).isRequired,
    history: HISTORY.isRequired,
    isLoading: func.isRequired,
    openModal: func.isRequired,
    sendPendoEvent: func.isRequired,
    unsubscribeUserFromSubscriptions: func.isRequired,
};

const mapDispatchToProps = {
    checkUserSubscriptionStatus: userActions.checkUserSubscriptionStatus,
    fetchBranding: brandingActions.fetchBranding,
    isLoading: uiActions.isLoading,
    openModal: uiActions.openModal,
    sendPendoEvent: pendoActions.sendPendoEvent,
    unsubscribeUserFromSubscriptions: userActions.unsubscribeUserFromSubscriptions,
};

export { Unsubscribe as UnsubscribeUnwrapped };
export default connect(null, mapDispatchToProps)(Unsubscribe);
